// Platform-agnostic reactions projection for the chat surface.
//
// Reactions are stored as an OR-set of `ReactionRecord` rows (one row
// per distinct `(target, emoji, actor)` tuple — see
// `./persistenceAdapters.ts`). This hook collapses those rows into the
// per-message `ReactionAggregate[]` shape the `MessageBubble` /
// `ReactionsRow` render: one entry per emoji on a message, with the
// reactor count and a `reactedByMe` flag.
//
// Like the other sync hooks here, it is host-neutral: it reads/writes
// nothing itself, it just subscribes to the supplied `ReactionsPersister`
// (Dexie liveQuery on web, the equivalent slice on desktop) and
// re-projects on every change. When no persister is supplied (a host
// that hasn't wired reactions yet) it returns a stable empty map so the
// caller can spread it unconditionally.
//
// Keying: the OR-set rows are anchored on the target's **content hash**
// (so a reaction that arrives before its target still lands in the right
// bucket), but the chat surface keys its sidecar by local message id.
// We resolve `messageId` from each row's `targetMessageId`; rows that
// haven't been resolved yet (no `targetMessageId`) are skipped — they
// project in once the message-side write back-fills the id.

import { useEffect, useMemo, useState } from 'react';

import type { ReactionAggregate } from '../chat/ReactionsRow';
import type { ReactionRecord, ReactionsPersister } from './persistenceAdapters';

const EMPTY: Readonly<Record<string, ReactionAggregate[]>> = Object.freeze({});

/**
 * Build the `messageId -> ReactionAggregate[]` map the chat surface
 * renders from the persisted OR-set.
 *
 * @param conversationId  Conversation whose reactions to project.
 * @param persister       OR-set backing store; when omitted the hook
 *                        returns a frozen empty map (no subscription).
 * @param selfAccountId   The local user's accountId — drives
 *                        `reactedByMe`. When omitted, `reactedByMe` is
 *                        always false.
 */
export function useReactions(
  conversationId: string,
  persister?: ReactionsPersister,
  selfAccountId?: string,
): Record<string, ReactionAggregate[]> {
  const [rows, setRows] = useState<readonly ReactionRecord[]>([]);

  useEffect(() => {
    if (!persister) {
      setRows([]);
      return;
    }

    let cancelled = false;

    // Prefer the live subscription when the persister offers one;
    // otherwise fall back to a single read so a non-reactive host still
    // gets an initial projection.
    if (persister.subscribe) {
      const unsubscribe = persister.subscribe(conversationId, (next) => {
        if (!cancelled) setRows(next);
      });
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    void persister
      .listByConversation(conversationId)
      .then((next) => {
        if (!cancelled) setRows(next);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, persister]);

  return useMemo(() => {
    if (!persister || rows.length === 0) return EMPTY as Record<string, ReactionAggregate[]>;
    return projectReactions(rows, selfAccountId);
  }, [persister, rows, selfAccountId]);
}

/**
 * Collapse OR-set rows into `messageId -> ReactionAggregate[]`.
 *
 * OR-set semantics: the rows are already deduped on
 * `(target, emoji, actor)` by the persister's compound key, so each row
 * is exactly one reactor's reaction. We group by `(messageId, emoji)`,
 * count rows, and flip `reactedByMe` when any grouped row's actor matches
 * `selfAccountId`. Rows without a resolved `targetMessageId` can't be
 * keyed onto a bubble yet, so they're skipped.
 */
function projectReactions(
  rows: readonly ReactionRecord[],
  selfAccountId?: string,
): Record<string, ReactionAggregate[]> {
  // messageId -> emoji -> { count, reactedByMe, reactors: [{ accountId, addedAt }] }
  const byMessage = new Map<
    string,
    Map<
      string,
      {
        count: number;
        reactedByMe: boolean;
        reactors: Array<{ accountId: string; addedAt: number }>;
      }
    >
  >();

  for (const row of rows) {
    const messageId = row.targetMessageId;
    if (messageId === undefined) continue;

    let byEmoji = byMessage.get(messageId);
    if (!byEmoji) {
      byEmoji = new Map();
      byMessage.set(messageId, byEmoji);
    }

    const existing = byEmoji.get(row.emoji);
    const reactedByMe = selfAccountId !== undefined && row.actorAccountId === selfAccountId;
    const reactor = { accountId: row.actorAccountId, addedAt: row.addedAt };
    if (existing) {
      existing.count += 1;
      existing.reactedByMe = existing.reactedByMe || reactedByMe;
      existing.reactors.push(reactor);
    } else {
      byEmoji.set(row.emoji, { count: 1, reactedByMe, reactors: [reactor] });
    }
  }

  const out: Record<string, ReactionAggregate[]> = {};
  for (const [messageId, byEmoji] of byMessage) {
    const aggregates: ReactionAggregate[] = [];
    for (const [emoji, { count, reactedByMe, reactors }] of byEmoji) {
      // Stable order: earliest reactor first (matches Figma's list order).
      const reactorAccountIds = reactors
        .slice()
        .sort((a, b) => a.addedAt - b.addedAt)
        .map((r) => r.accountId);
      aggregates.push({ emoji, count, reactedByMe, reactorAccountIds });
    }
    out[messageId] = aggregates;
  }
  return out;
}
